// Central SEO copy, keyword bank and structured data helpers for RPLC UAE.
// Keywords are selected from real UAE + India intent (VARA, PDPL, DPDPA, DIFC, ADGM, VASP,
// TGRAF, crypto lawyer Dubai, cyber law Pune, DPDP compliance, AI governance UAE, etc.)

export const SITE = {
  origin: "https://rplcuae.com",
  brand: "Rane Pingle Law Chambers",
  brandShort: "RPLC UAE",
  legalEntity: "Rane Pingle and Partners FZ LLC",
  defaultOgImage: "https://rplcuae.com/media/brand/rplc-logo.svg",
};

export const CORE_KEYWORDS = [
  // UAE — jurisdictional
  "law firm Dubai", "law firm UAE", "Ras Al Khaimah law firm", "RAKEZ law firm",
  "DIFC lawyer", "ADGM lawyer", "Dubai lawyer", "UAE lawyer",
  // UAE — technology & regulation
  "VARA lawyer Dubai", "VARA compliance", "VASP lawyer UAE", "TGRAF assessment",
  "crypto lawyer Dubai", "crypto lawyer UAE", "Web3 lawyer Dubai", "digital assets lawyer UAE",
  "AI governance lawyer UAE", "EU AI Act UAE",
  // UAE — data protection
  "UAE PDPL lawyer", "UAE data protection lawyer", "DPO services UAE", "DIFC data protection",
  "ADGM data protection", "cyber security lawyer Dubai", "cyber crime lawyer UAE",
  // UAE — financial services
  "fintech lawyer Dubai", "DFSA lawyer", "FSRA lawyer", "CBUAE lawyer",
  "M&A lawyer Dubai", "corporate lawyer Dubai", "commercial contracts lawyer UAE",
  // India — jurisdictional
  "law firm Pune", "law firm Mumbai", "cyber law firm India", "technology law firm India",
  // India — data protection
  "DPDPA lawyer", "DPDPA compliance lawyer", "DPDP consultant India", "data protection lawyer India",
  "data privacy lawyer Pune", "cyber crime lawyer Pune", "cyber law lawyer Mumbai",
  // India — commercial
  "startup lawyer India", "startup lawyer Pune", "SaaS lawyer India", "FEMA lawyer India",
  "FDI lawyer India", "cross-border lawyer India UAE",
  // Cross-border
  "cross-border lawyer UAE India USA", "India entry lawyer Dubai", "UAE India investment lawyer",
  // Brand
  "Rane Pingle Law Chambers", "RPLC UAE", "Raunak Rane lawyer", "Rajas Pingle cyber lawyer",
];

export function joinKw(arr) {
  return Array.from(new Set(arr)).join(", ");
}

// ---------- JSON-LD helpers ----------
export function ldLegalService() {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": SITE.brand,
    "alternateName": ["RPLC UAE", "Rane Pingle and Partners FZ LLC"],
    "url": SITE.origin,
    "logo": SITE.defaultOgImage,
    "image": SITE.defaultOgImage,
    "description": "RPLC UAE is a modern legal & business consultancy at the intersection of Law × Technology × Regulation × Business — advising on cyber crime & digital fraud, economic offences, CFD and online trading disputes, VARA, UAE PDPL, DIFC/ADGM, DPDPA, AI governance, corporate and cross-border M&A across UAE, India and USA.",
    "areaServed": [
      { "@type": "Country", "name": "United Arab Emirates" },
      { "@type": "Country", "name": "India" },
      { "@type": "Country", "name": "United States" },
    ],
    "knowsAbout": [
      "Cyber crime",
      "Digital fraud recovery",
      "Cryptocurrency scam recovery",
      "Economic offences",
      "White-collar crime defence",
      "CFD trading disputes",
      "Forex broker disputes",
      "VARA compliance",
      "UAE PDPL",
      "DIFC data protection",
      "ADGM data protection",
      "DPDPA India",
      "AI governance",
      "Cyber law",
      "Cyber security",
      "Digital forensics",
      "Cross-border M&A",
      "Corporate & Commercial law",
      "FEMA / FDI advisory",
      "Startup fundraising",
    ],
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "RAKEZ Business Zone, Al Nakheel Area, P.O. Box No. 10055",
        "addressLocality": "Ras Al Khaimah",
        "addressCountry": "AE",
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "Boulevard Plaza, Level 3, Sheikh Mohammed bin Rashid Blvd",
        "addressLocality": "Dubai",
        "addressCountry": "AE",
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "Bhuvaneshwari Apartments, 501, above P-cube House",
        "addressLocality": "Pune",
        "addressRegion": "Maharashtra",
        "postalCode": "411008",
        "addressCountry": "IN",
      },
    ],
    "contactPoint": [{
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "chambers@rplc.ae",
      "areaServed": ["AE", "IN", "US"],
      "availableLanguage": ["English", "Arabic", "Hindi", "Marathi"],
    }],
    "sameAs": ["https://www.rplawchambers.com"],
  };
}

export function ldOrganization() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE.legalEntity,
    "alternateName": SITE.brand,
    "url": SITE.origin,
    "logo": SITE.defaultOgImage,
  };
}

export function ldPerson(profile) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": profile.name,
    "jobTitle": profile.role,
    "email": profile.email,
    "telephone": profile.phone,
    "image": SITE.origin + profile.img,
    "knowsAbout": profile.focus.split("·").map((s) => s.trim()),
    "worksFor": {
      "@type": "LegalService",
      "name": SITE.brand,
      "url": SITE.origin,
    },
    "alumniOf": (profile.education || []).map((e) => ({
      "@type": "EducationalOrganization",
      "name": e.school,
    })),
    "url": `${SITE.origin}/team/${profile.slug}`,
  };
}

export function ldArticle(article, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.dek,
    "author": { "@type": "Person", "name": article.author },
    "publisher": ldOrganization(),
    "datePublished": article.date,
    "articleSection": article.cat,
    "mainEntityOfPage": SITE.origin + path,
  };
}

export function ldBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": SITE.origin + it.path,
    })),
  };
}

export function ldFaq(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}

export function ldService(area, path, description) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": area.title,
    "name": `${area.title} · ${SITE.brand}`,
    "description": description,
    "url": SITE.origin + path,
    "provider": { "@type": "LegalService", "name": SITE.brand, "url": SITE.origin },
    "areaServed": [
      { "@type": "Country", "name": "United Arab Emirates" },
      { "@type": "Country", "name": "India" },
    ],
    "availableLanguage": ["English", "Arabic", "Hindi"],
  };
}
